// Camada de autenticação do web. Fala SOMENTE com a nossa API — o Keycloak fica
// atrás dela e nunca é aberto no navegador.
//
// Os tokens ficam em localStorage para a sessão sobreviver a um F5. Isso os expõe
// a XSS; a alternativa (cookie httpOnly) exige a API emitindo cookie e um mecanismo
// de CSRF. Enquanto for um app interno atrás de VPN, o custo não se paga — mas é
// uma dívida consciente, não um descuido.

import { apiBaseUrl } from './apiUrl';

const ACCESS_KEY = 'castanha.accessToken';
const REFRESH_KEY = 'castanha.refreshToken';

export interface UserProfile {
  username?: string;
  name?: string;
  email?: string;
  roles: string[];
}

export const tokenStore = {
  access: () => (typeof window === 'undefined' ? null : localStorage.getItem(ACCESS_KEY)),
  refresh: () => (typeof window === 'undefined' ? null : localStorage.getItem(REFRESH_KEY)),
  save(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// Extrai o payload do JWT sem validar assinatura: é só para desenhar a UI
// (nome, papéis). Quem valida de verdade é a API, a cada requisição.
export function decodeToken(token: string): UserProfile | null {
  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('')
      )
    );
    return {
      username: json.preferred_username,
      name: json.name,
      email: json.email,
      roles: json.realm_access?.roles ?? [],
    };
  } catch {
    return null;
  }
}

export function isExpired(token: string, skewSeconds = 30): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp !== 'number' || payload.exp * 1000 - skewSeconds * 1000 < Date.now();
  } catch {
    return true;
  }
}

// A API responde 400 com { error } ou { error, errors: { Campo: [msgs] } }.
async function readError(resp: Response, fallback: string): Promise<string> {
  try {
    const body = await resp.json();
    if (body.errors) {
      const first = Object.values(body.errors)[0];
      if (Array.isArray(first) && first.length) return String(first[0]);
    }
    return body.error ?? fallback;
  } catch {
    return fallback;
  }
}

export async function login(email: string, password: string): Promise<void> {
  const resp = await fetch(`${apiBaseUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Não foi possível entrar.'));

  const { accessToken, refreshToken } = await resp.json();
  tokenStore.save(accessToken, refreshToken);
}

export async function register(email: string, password: string): Promise<void> {
  const resp = await fetch(`${apiBaseUrl()}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Não foi possível criar a conta.'));
}

// A API sempre responde 204 (exista ou não o email) — mesma lógica do login, para não
// revelar quais emails têm conta. O único erro possível aqui é formato inválido.
export async function forgotPassword(email: string): Promise<void> {
  const resp = await fetch(`${apiBaseUrl()}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Não foi possível processar o pedido.'));
}

// Renova o access token. Retorna o novo, ou null se a sessão morreu.
export async function refreshSession(): Promise<string | null> {
  const refreshToken = tokenStore.refresh();
  if (!refreshToken) return null;

  const resp = await fetch(`${apiBaseUrl()}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!resp.ok) {
    tokenStore.clear();
    return null;
  }

  const data = await resp.json();
  tokenStore.save(data.accessToken, data.refreshToken);
  return data.accessToken as string;
}

export function logout(): void {
  tokenStore.clear();
}
